import React, { useState, useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';

import AppLandingPageLayout from "./layout/AppLandingPage/AppLandingPageLayout";
import SessionReact, { getUserId } from "supertokens-auth-react/recipe/session";
import DownloadsPage from "./pages/AppLandingPage/DownloadsPage";
import HomePage from "./pages/AppLandingPage/HomePage";
import PricingPage from "./pages/AppLandingPage/PricingPage";
import CalculatorPage from "./pages/AppLandingPage/CalculatorPage";
import PrivacyPolicy from "./components/SdkLandingPageComponents/landingpage/components/PrivacyPolicy";
import ReturnPolicy from "./components/SdkLandingPageComponents/landingpage/components/ReturnPolicy";
import TermsOfService from "./components/SdkLandingPageComponents/landingpage/components/TermsOfService";
import NotFound from "./pages/NotFound";
import SDKLayout from "./layout/SDKLayout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SubmitForm from "./components/AppLandingPageComponents/tallyForms/Form";
import ForQA from "./components/AppLandingPageComponents/landingPageComponents/AppCards/ForQA";
import ForDevelopers from "./components/AppLandingPageComponents/landingPageComponents/AppCards/ForDevelopers";
import ForProductManagers from "./components/AppLandingPageComponents/landingPageComponents/AppCards/ForProductManagers";
import Login from "./auth/sdkLandingPage/login";
import AppThirdPartyAuth from "./auth/sdkLandingPage/AppThirdPartyAuth";
import WebGithubAuth from "./auth/sdkLandingPage/WebGithubAuth";
import AppGithubAuth from "./auth/sdkLandingPage/AppGithubAuth";
import WebGoogleAuth from "./auth/sdkLandingPage/WebGoogleAuth";
import PlaygroundPage from "./pages/AppLandingPage/PlaygroundPage/PlaygroundPage";
import {
  appAccount,
  sdkChangePwd,
  appLogin,
  sdkVerifyEmail,
} from "./routes/routes";
import Session from "supertokens-web-js/recipe/session";
import { getAxiosInstance } from "../src/api/services/apiService";
import SdkDashboardLayout from "./layout/SdkLandingPage/SdkDashboardLayout";
import SuperTokens from "supertokens-auth-react";
import ThirdPartyEmailPassword from "supertokens-auth-react/recipe/thirdpartyemailpassword";
import Billing from "./components/SdkLandingPageComponents/billing";
import Profile from "./components/SdkLandingPageComponents/profile";
import Signup from "./auth/sdkLandingPage/signup";
import FeaturesPage from "./components/AppLandingPageComponents/landingPageComponents/AppCards/FeaturesPage";
import WhyAutoFlow from "./components/AppLandingPageComponents/landingPageComponents/AppCards/WhyAutoFlow";
import EmailConfirmation from "./auth/sdkLandingPage/EmailConfirmation";
import ReportLayout from "./layout/ReportLayout/reportLayout";
import ReportPage from "./pages/AppLandingPage/ReportPage";
import StudioAuthPage from "./pages/AppLandingPage/StudioAuthPage";
import AppCreateAccount from "./auth/sdkLandingPage/AppCreateAccount";
import AnalyticsWrapper from "./components/SdkLandingPageComponents/analytics/AnalyticsWrapper";
import { TestProvider } from "./context/TestContext";


SuperTokens.init({
  appInfo: {
    appName: "autoflow",
    apiDomain: process.env.REACT_APP_AUTH_BASE_URL,
    websiteDomain: process.env.REACT_APP_WEBSITE_DOMAIN,
    apiBasePath: "/auth",
    websiteBasePath: "/auth",
  },
  recipeList: [
    ThirdPartyEmailPassword.init(),
    process.env.REACT_APP_WEBSITE_DOMAIN === "http://localhost:3000"
      ? SessionReact.init()
      : SessionReact.init({ sessionTokenFrontendDomain: ".autoflow.tools" }),
  ],
});

function setCookie(name, value) {
  document.cookie = `${name}=${value}; path=/`;
}

const App = () => {

  getAxiosInstance();
  const [userId, setUserId] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [doesSessionExistState, setDoesSessionExistState] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const id = await getUserId();
        setUserId(id);
      } catch (error) {
        console.error("Error fetching user ID:", error);
      }
    })();

    (async () => {
      if (await Session.doesSessionExist()) {
        setDoesSessionExistState(true);
      }
    })();
    setCookie('work-with-us', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc0NyYWNrZWQiOiJZRVMhIENvbmdyYXRzIG9uIGZpbmlzaGluZyB0aGUgZmlyc3Qgc3RlcC4gTm93IGdvIHRvIHRoZSBuZXh0IHN0ZXAiLCJuZXh0U3RlcCI6IlYyOTNJU0JaYjNVZ1kzSmhZMnRsWkNCcGRDRUtDa2R2YjJRZ1oyOXBibWNzSUc1bGVIUWdjM1J2Y0RvS1ZWSk1PaUJvZEhSd2N6b3ZMM1Z6TFdObGJuUnlZV3d4TFdGMWRHOW1iRzkzTFhOMGRXUnBieTVqYkc5MVpHWjFibU4wYVc5dWN5NXVaWFF2WVdZdFlYTnphV2R1YldWdWRDMXNNek4wQ2sxbGRHaHZaRG9nVUU5VFZBcEtVMDlPSUVKdlpIazZJQXA3Q21KaGMyVTJOSE4wY21sdVp5d0tjR2h2Ym1WT2RXMWlaWElLZlFvS1RrOVVSVG9nVlhObElIQm9iMjVsSUc1MWJXSmxjaUIzYVhSb2IzVjBJR052ZFc1MGNua2dZMjlrWlNCc2FXdGxJQ3M1TVFvS0NnPT0iLCJpYXQiOjE3MzMyMzExOTd9.du8HlV37HNzu53irwIhCxKsAwSyRCznfd-Bg-PZyJDs')
  }, []);

  const PrivateRoute = ({ children }) => {
    const navigate = useNavigate();
    const sessionExists = doesSessionExistState;

    useEffect(() => {
      (async () => {
        if (sessionExists) {
          if (isEmailVerified) {
            return true;
          } else {
            navigate(sdkVerifyEmail);
          }
        } else {
          // console.log("Session does not exist, redirecting to login");
          navigate(appLogin);
        }
      })();
    }, [sessionExists]);

    return sessionExists ? children : null;
  };

  const PublicRoute = ({ children }) => {
    const navigate = useNavigate();
    const sessionExists = doesSessionExistState;

    useEffect(() => {
      (async () => {
        if (sessionExists) {
          // console.log("Session exists, redirecting to account");
          navigate(appAccount);
        }
      })();
    }, [navigate, sessionExists]);

    return !sessionExists ? children : null;
  };

  // check is email verified
  useEffect(() => {
    const checkisEmailVerified = async () => {
      if (await Session.doesSessionExist()) {
        try {
          let accessTokenPayload =
            await Session.getAccessTokenPayloadSecurely();
          let isEmailVerified = accessTokenPayload["st-ev"]["v"];
          console.log(isEmailVerified, "is Email verified from app.js");
          setIsEmailVerified(isEmailVerified);
          return isEmailVerified;
        } catch (error) {
          console.error(
            "Error occurred while getting email verification status:",
            error
          );
          return false;
        }
      } else {
        return false;
      }
    };

    checkisEmailVerified();
  }, []);

  return (
    
    <GoogleOAuthProvider clientId='812554579399-063lh3jk3r8loa7b0sah0ch6s7835lrd.apps.googleusercontent.com'>
      <TestProvider>
      <Router>
        <Routes>



          <Route path="/app/thirdparty/signinup" element={<ReportLayout />}>
            <Route index Component={StudioAuthPage} />
          </Route>

          <Route path="/app/auth/callback/thirdparty" element={<ReportLayout />}>
            <Route index Component={AppThirdPartyAuth} />
          </Route>

        <Route path="/app/auth/callback/thirdparty/github-web" element={<ReportLayout />}>
              <Route index Component={WebGithubAuth} />
        </Route>

        <Route path="/app/auth/callback/thirdparty/google-web" element={<ReportLayout />}>
              <Route index Component={WebGoogleAuth} />
        </Route>

        <Route path="/app/auth/callback/thirdparty/github-app" element={<ReportLayout />}>
              <Route index Component={AppGithubAuth} />
        </Route>


          <Route path="/app/create-account" element={<AppCreateAccount />} />


          <Route path="/" element={<AppLandingPageLayout />}>
            <Route path="downloads" Component={DownloadsPage} />
            <Route path="pricing" Component={PricingPage} />
            <Route path="playground" element={<PlaygroundPage />} />
            <Route path="contact-us" Component={SubmitForm} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfService />} />
            <Route path="return-policy" element={<ReturnPolicy />} />
            <Route path="for-QA" element={<ForQA />} />
            <Route path="features" element={<FeaturesPage />} />
            <Route path="whyAutoFlow" element={<WhyAutoFlow />} />
            <Route path="for-Dev" element={<ForDevelopers />} />
            <Route path="for-Product-manager" element={<ForProductManagers />} />

            <Route index Component={HomePage} />
            <Route path="/report" element={<ReportLayout />}>
              <Route index Component={ReportPage} />
            </Route>
            <Route path="calculator" element={<CalculatorPage />} />
          </Route>
          <Route path="/sdk/*" element={<SDKLayout />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login logFrom={'app'} />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <Signup logFrom={'app'} />
              </PublicRoute>
            }
          />
          <Route path="/verify-email" element={<EmailConfirmation logFrom={'app'} />} />
          <Route
            path="/account"
            element={
              <PrivateRoute>
                <SdkDashboardLayout logFrom={'app'} isEmailVerified={isEmailVerified} />
              </PrivateRoute>
            }
          >
            <Route path="analytics" element={<AnalyticsWrapper/>} />
            <Route path="billing" element={<Billing />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Router>
      <div>
        <ToastContainer
          position="top-right"
          autoClose={6000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          toastStyle={{
            width: "auto",
            fontFamily: "Inter Variable",
            fontWeight: "bold",
          }}
        />
      </div>
      </TestProvider>
    </GoogleOAuthProvider>);
};

export default App;

